import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { RegisterDialog } from "@/components/site/RegisterDialog";
import {
  type EventItem,
  resolveEventImageUrl,
  getEventStatusRange,
  formatEventDateRange,
} from "@/lib/events";
import { Calendar, MapPin, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";

export const Route = createFileRoute("/events/$eventId")({
  component: EventDetail,
  head: ({ params }) => ({
    meta: [{ title: `Event Details — ${params.eventId}` }],
  }),
});

function EventDetail() {
  const { eventId } = Route.useParams();
  const { selectEvent } = useSelectedEvent();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  const loadEvent = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const response = await api.get<EventItem>(`/api/events/${eventId}`);
      if (response.success && response.data) {
        setEvent(response.data);
        selectEvent(response.data.id);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHero title="Loading..." />
        <section className="py-16 px-4 text-center text-muted-foreground">Loading event...</section>
      </>
    );
  }

  if (notFound || !event) {
    return (
      <>
        <PageHero title="Event Not Found" />
        <section className="py-16 px-4 text-center">
          <p className="text-muted-foreground mb-6">This event could not be found.</p>
          <Button asChild>
            <Link to="/">Back to Events</Link>
          </Button>
        </section>
      </>
    );
  }

  const status = getEventStatusRange(event);
  const imageUrl = resolveEventImageUrl(event.image_url);
  const isPast = status === "past";

  return (
    <>
      <PageHero
        eyebrow={event.category || "UASA Event"}
        title={event.title}
        subtitle={event.subtitle || undefined}
      />
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Button asChild variant="ghost" className="mb-6 -ml-2 text-muted-foreground">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" /> All Events
            </Link>
          </Button>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src={imageUrl} alt={event.title} className="w-full h-auto object-cover" />
            </div>

            <Card className="p-6 sm:p-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {status !== "unknown" && (
                  <Badge variant={status === "upcoming" ? "default" : "secondary"}>
                    {status === "upcoming" ? "Upcoming" : "Past"}
                  </Badge>
                )}
                {event.category && <Badge variant="outline">{event.category}</Badge>}
              </div>

              {(event.event_date_from || event.event_date) && (
                <p className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Calendar className="w-4 h-4 text-accent" />
                  {formatEventDateRange(event)}
                </p>
              )}
              {event.location && (
                <p className="flex items-center gap-2 text-muted-foreground mb-6">
                  <MapPin className="w-4 h-4 text-accent" />
                  {event.location}
                </p>
              )}

              {event.description && (
                <div
                  className="text-muted-foreground leading-relaxed mb-8 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                {!isPast && (
                  <RegisterDialog
                    triggerLabel="Register Now"
                    triggerSize="lg"
                    triggerClassName="bg-accent hover:bg-accent/90 text-accent-foreground"
                    eventId={event.id}
                    eventTitle={event.title}
                  />
                )}
                <Button asChild size="lg" variant="outline" className={isPast ? "sm:flex-1" : ""}>
                  <Link to="/agenda" onClick={() => selectEvent(event.id)}>View Agenda</Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
}
